import { useContext, useLayoutEffect, useRef } from 'react'

import { PortalContext } from 'core-components/library/popup'

/**
 * Remembers the element that was focused when the dialog opened (its trigger in most cases)
 * and returns focus to it on close, so keyboard navigation continues from the same place.
 *
 * A layout effect is used on purpose: it captures the trigger before the dialog takes focus
 * and returns focus while the dialog node is still in the document.
 */
export const useFocusReturn = () => {
  const triggerRef = useRef<HTMLElement | null>(null)
  const { rootNode } = useContext(PortalContext)

  useLayoutEffect(() => {
    const { activeElement } = rootNode

    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      triggerRef.current = activeElement
    }

    return () => {
      const trigger = triggerRef.current
      triggerRef.current = null

      // the trigger is gone when it was rendered by something the dialog replaced,
      // a menu item closing its menu on click for example
      if (trigger?.isConnected) {
        trigger.focus()
      }
    }
  }, [rootNode])
}
